import React from 'react';
import { Search, BookOpen, Clock, CheckCircle, Video, FileText, Link, Target } from 'lucide-react';

const TrainingTab = ({ trainingResources, trainingCategories, trainingFilters, setTrainingFilters, loadTrainingResources, updateTrainingProgress }) => (
  <div className="space-y-6">
    {/* Search and Filters */}
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center flex-1 min-w-64">
          <Search className="w-4 h-4 mr-2 text-gray-500" />
          <input
            type="text"
            value={trainingFilters.search}
            onChange={(e) => setTrainingFilters(prev => ({ ...prev, search: e.target.value }))}
            placeholder="Search training resources..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>

        <select
          value={trainingFilters.category_id}
          onChange={(e) => setTrainingFilters(prev => ({ ...prev, category_id: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="">All Categories</option>
          {trainingCategories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>

        <select
          value={trainingFilters.training_type}
          onChange={(e) => setTrainingFilters(prev => ({ ...prev, training_type: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="">All Types</option>
          <option value="video">Video</option>
          <option value="pdf">PDF</option>
          <option value="link">External Link</option>
          <option value="interactive">Interactive</option>
        </select>

        <button
          onClick={loadTrainingResources}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
        >
          Apply Filters
        </button>
      </div>
    </div>

    {/* Training Resources */}
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <BookOpen className="w-5 h-5 mr-2 text-purple-500" />
        Training Library ({trainingResources.length})
      </h3>

      {trainingResources.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No training resources found</p>
          <p className="text-sm">Try adjusting your search filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {trainingResources.map((resource) => (
            <div key={resource.id} className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {resource.training_type === 'video' && <Video className="w-8 h-8 text-red-500" />}
                  {resource.training_type === 'pdf' && <FileText className="w-8 h-8 text-blue-500" />}
                  {resource.training_type === 'link' && <Link className="w-8 h-8 text-green-500" />}
                  {resource.training_type === 'interactive' && <Target className="w-8 h-8 text-purple-500" />}
                </div>

                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {resource.title}
                        {resource.is_mandatory && (
                          <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                            Required
                          </span>
                        )}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        {resource.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center mt-2 text-xs text-gray-500 gap-4">
                    <span className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {resource.duration_minutes} min
                    </span>
                    <span className={`px-2 py-1 rounded ${
                      resource.difficulty_level === 'beginner' ? 'bg-green-100 text-green-800' :
                      resource.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {resource.difficulty_level}
                    </span>
                  </div>

                  {resource.tags && resource.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {resource.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                      {resource.completion_rate.toFixed(0)}% completion rate
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => updateTrainingProgress(resource.id, {
                          status: 'in_progress',
                          progress_percentage: 0
                        })}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Start
                      </button>
                      <button className="px-3 py-1 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default TrainingTab;
